import { useState } from "react";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "../components/ui/checkbox";
import { submitLead } from "../lib/api";
import { customerTypes, productOptions } from "../lib/data";
import useSEO from "../lib/seo";

const initial = {
  product_required: "",
  full_name: "",
  company_name: "",
  mobile: "",
  email: "",
  customer_type: "",
  city: "",
  // Loan
  loan_amount: "",
  purpose: "",
  existing_banking_relationship: "",
  // Insurance
  insurance_type: "",
  sum_assured: "",
  age: "",
  family_members: "",
  existing_policy: "",
  // Investments
  investment_type: "",
  monthly_investment: "",
  lumpsum_amount: "",
  investment_horizon: "",
  risk_appetite: "",
  investment_goal: "",
  // Common
  message: "",
  consent: false,
};

const inputCls =
  "w-full bg-[#1a1a1a] border border-white/10 text-white placeholder-neutral-500 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#dc2626] focus:ring-1 focus:ring-[#dc2626] transition-colors";
const labelCls =
  "block text-xs font-bold tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono";

const insuranceTypes = ["Life", "Health", "Motor / Vehicle", "Term", "General"];
const investmentTypes = ["SIP (Monthly)", "Lumpsum", "Both SIP & Lumpsum"];
const horizons = [
  "Less than 1 year",
  "1 – 3 years",
  "3 – 5 years",
  "5 – 10 years",
  "10+ years",
];
const riskProfiles = ["Conservative", "Moderate", "Aggressive"];
const investmentGoals = [
  "Wealth Creation",
  "Retirement",
  "Child's Education",
  "Tax Saving (ELSS)",
  "Home Purchase",
  "Emergency Fund",
  "Other",
];

const isInsurance = (p) => p === "Insurance";
const isInvestment = (p) => p === "Mutual Funds / SIP";
const isLoan = (p) => p && !isInsurance(p) && !isInvestment(p);

export default function RequestAccess() {
  useSEO({
    title: "Request Access — Apply for a Loan or Insurance",
    description:
      "Apply online for personal, home, business or mortgage loans, doctor & professional loans, insurance or investments. Submit your requirement and our team will connect you with the right lender.",
    path: "/request-access",
  });
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.product_required) e.product_required = "Please select a product";
    if (!form.full_name.trim()) e.full_name = "Full name is required";
    if (!form.mobile.trim() || !/^[0-9+\-\s]{7,15}$/.test(form.mobile))
      e.mobile = "Valid mobile number is required";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Valid email is required";
    if (!form.customer_type) e.customer_type = "Select a customer type";
    if (!form.consent) e.consent = "Consent is required to submit";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead(form);
      setSuccess(true);
      toast.success("Request received. Our team will reach out shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="page-request-access-success"
        className="min-h-[70vh] flex items-center justify-center px-4"
      >
        <div className="max-w-xl w-full text-center border border-white/10 rounded-2xl bg-[#121212] p-10 sm:p-14">
          <div className="mx-auto h-16 w-16 rounded-full bg-[#dc2626]/10 flex items-center justify-center text-[#dc2626]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-display font-bold text-3xl sm:text-4xl text-white tracking-tighter">
            Request received.
          </h1>
          <p className="mt-4 text-neutral-300 leading-relaxed">
            Thank you for reaching out to DVP DIGI WORLD. A member of our team
            will connect with you shortly to understand your requirement and
            match you with the right financial partner.
          </p>
          <button
            onClick={() => setSuccess(false)}
            data-testid="submit-another-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white px-6 py-3 font-semibold"
          >
            Submit another request <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const product = form.product_required;

  return (
    <div data-testid="page-request-access">
      {/* Hero */}
      <section className="relative border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#dc2626]/15 via-transparent to-[#1d4ed8]/15" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#dc2626]">
            Request Access
          </div>
          <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white max-w-4xl leading-[0.95]">
            Request Access to Financial Products
          </h1>
          <p className="mt-6 text-base sm:text-lg text-neutral-300 max-w-3xl leading-relaxed">
            Looking for the right financial solution? Submit your requirement
            and our team will connect you with suitable lending, insurance or
            investment options through our network of trusted institutions.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form
            onSubmit={onSubmit}
            data-testid="request-access-form"
            className="border border-white/10 rounded-2xl bg-[#0f0f0f] p-5 sm:p-10 space-y-6"
          >
            {/* PRODUCT REQUIRED — FIRST */}
            <div>
              <label className={labelCls}>Product Required *</label>
              <select
                className={`${inputCls} text-base font-semibold`}
                value={form.product_required}
                onChange={(e) => update("product_required", e.target.value)}
                data-testid="ra-select-product"
              >
                <option value="">— Select a product —</option>
                {productOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              {errors.product_required && (
                <p className="text-xs text-[#dc2626] mt-2">
                  {errors.product_required}
                </p>
              )}
            </div>

            {/* COMMON CONTACT FIELDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input
                  className={inputCls}
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  placeholder="Your full name"
                  data-testid="ra-input-full-name"
                />
                {errors.full_name && (
                  <p className="text-xs text-[#dc2626] mt-2">{errors.full_name}</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Customer Type *</label>
                <select
                  className={inputCls}
                  value={form.customer_type}
                  onChange={(e) => update("customer_type", e.target.value)}
                  data-testid="ra-select-customer-type"
                >
                  <option value="">Select customer type</option>
                  {customerTypes.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {errors.customer_type && (
                  <p className="text-xs text-[#dc2626] mt-2">
                    {errors.customer_type}
                  </p>
                )}
              </div>

              <div>
                <label className={labelCls}>Mobile Number *</label>
                <input
                  className={inputCls}
                  value={form.mobile}
                  onChange={(e) => update("mobile", e.target.value)}
                  placeholder="10-digit mobile number"
                  inputMode="numeric"
                  data-testid="ra-input-mobile"
                />
                {errors.mobile && (
                  <p className="text-xs text-[#dc2626] mt-2">{errors.mobile}</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Email Address *</label>
                <input
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  data-testid="ra-input-email"
                />
                {errors.email && (
                  <p className="text-xs text-[#dc2626] mt-2">{errors.email}</p>
                )}
              </div>

              <div>
                <label className={labelCls}>City</label>
                <input
                  className={inputCls}
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  placeholder="e.g. Chennai"
                  data-testid="ra-input-city"
                />
              </div>
              {!isInsurance(product) && !isInvestment(product) && (
                <div>
                  <label className={labelCls}>Company Name</label>
                  <input
                    className={inputCls}
                    value={form.company_name}
                    onChange={(e) => update("company_name", e.target.value)}
                    placeholder="Optional"
                    data-testid="ra-input-company"
                  />
                </div>
              )}
            </div>

            {/* PRODUCT-SPECIFIC SECTIONS */}
            {isLoan(product) && (
              <ProductBlock
                title="Loan Details"
                subtitle="Tell us about your loan requirement"
                accent="red"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label className={labelCls}>Loan Amount Required</label>
                    <input
                      className={inputCls}
                      value={form.loan_amount}
                      onChange={(e) => update("loan_amount", e.target.value)}
                      placeholder="e.g. ₹10,00,000"
                      data-testid="ra-input-amount"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Existing Banking Relationship</label>
                    <input
                      className={inputCls}
                      value={form.existing_banking_relationship}
                      onChange={(e) =>
                        update("existing_banking_relationship", e.target.value)
                      }
                      placeholder="e.g. HDFC Bank, ICICI Bank"
                      data-testid="ra-input-banking"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Purpose of Funding</label>
                    <input
                      className={inputCls}
                      value={form.purpose}
                      onChange={(e) => update("purpose", e.target.value)}
                      placeholder="e.g. Business expansion, home purchase, working capital"
                      data-testid="ra-input-purpose"
                    />
                  </div>
                </div>
              </ProductBlock>
            )}

            {isInsurance(product) && (
              <ProductBlock
                title="Insurance Details"
                subtitle="Help us recommend the right cover"
                accent="blue"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label className={labelCls}>Insurance Type</label>
                    <select
                      className={inputCls}
                      value={form.insurance_type}
                      onChange={(e) => update("insurance_type", e.target.value)}
                      data-testid="ra-select-insurance-type"
                    >
                      <option value="">Select insurance type</option>
                      {insuranceTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Sum Assured / Cover</label>
                    <input
                      className={inputCls}
                      value={form.sum_assured}
                      onChange={(e) => update("sum_assured", e.target.value)}
                      placeholder="e.g. ₹50,00,000"
                      data-testid="ra-input-sum-assured"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Age</label>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={100}
                      className={inputCls}
                      value={form.age}
                      onChange={(e) => update("age", e.target.value)}
                      placeholder="e.g. 32"
                      data-testid="ra-input-age"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Family Members to Cover</label>
                    <input
                      className={inputCls}
                      value={form.family_members}
                      onChange={(e) => update("family_members", e.target.value)}
                      placeholder="e.g. Self, Spouse, 2 kids"
                      data-testid="ra-input-family"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Existing Policy?</label>
                    <select
                      className={inputCls}
                      value={form.existing_policy}
                      onChange={(e) => update("existing_policy", e.target.value)}
                      data-testid="ra-select-existing-policy"
                    >
                      <option value="">Select</option>
                      <option value="No">No existing policy</option>
                      <option value="Yes - Want to add new">
                        Yes — adding a new policy
                      </option>
                      <option value="Yes - Want to switch">
                        Yes — looking to switch / renew
                      </option>
                    </select>
                  </div>
                </div>
              </ProductBlock>
            )}

            {isInvestment(product) && (
              <ProductBlock
                title="Investment Details"
                subtitle="Tell us about your investment goals"
                accent="blue"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <label className={labelCls}>Investment Type</label>
                    <select
                      className={inputCls}
                      value={form.investment_type}
                      onChange={(e) => update("investment_type", e.target.value)}
                      data-testid="ra-select-investment-type"
                    >
                      <option value="">Select type</option>
                      {investmentTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Investment Goal</label>
                    <select
                      className={inputCls}
                      value={form.investment_goal}
                      onChange={(e) => update("investment_goal", e.target.value)}
                      data-testid="ra-select-investment-goal"
                    >
                      <option value="">Select goal</option>
                      {investmentGoals.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Monthly Investment (SIP)</label>
                    <input
                      className={inputCls}
                      value={form.monthly_investment}
                      onChange={(e) => update("monthly_investment", e.target.value)}
                      placeholder="e.g. ₹10,000"
                      data-testid="ra-input-monthly-investment"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Lumpsum Amount</label>
                    <input
                      className={inputCls}
                      value={form.lumpsum_amount}
                      onChange={(e) => update("lumpsum_amount", e.target.value)}
                      placeholder="e.g. ₹2,00,000"
                      data-testid="ra-input-lumpsum"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Investment Horizon</label>
                    <select
                      className={inputCls}
                      value={form.investment_horizon}
                      onChange={(e) => update("investment_horizon", e.target.value)}
                      data-testid="ra-select-horizon"
                    >
                      <option value="">Select horizon</option>
                      {horizons.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Risk Appetite</label>
                    <select
                      className={inputCls}
                      value={form.risk_appetite}
                      onChange={(e) => update("risk_appetite", e.target.value)}
                      data-testid="ra-select-risk"
                    >
                      <option value="">Select risk profile</option>
                      {riskProfiles.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </ProductBlock>
            )}

            <div>
              <label className={labelCls}>Message / Specific Requirement</label>
              <textarea
                rows={4}
                className={inputCls}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us more about what you're looking for…"
                data-testid="ra-input-message"
              />
            </div>

            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.consent}
                  onCheckedChange={(v) => update("consent", !!v)}
                  data-testid="ra-checkbox-consent"
                  className="mt-1 border-white/20 data-[state=checked]:bg-[#dc2626] data-[state=checked]:border-[#dc2626]"
                />
                <span className="text-sm text-neutral-300 leading-relaxed">
                  I agree to be contacted by DVP DIGI WORLD regarding my
                  financial requirement.
                </span>
              </label>
              {errors.consent && (
                <p className="text-xs text-[#dc2626] mt-2">{errors.consent}</p>
              )}
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid="ra-submit-button"
                className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-4 font-semibold transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Request Access <ArrowUpRight className="h-5 w-5" />
                  </>
                )}
              </button>
              <span className="text-xs text-neutral-500 font-mono uppercase tracking-[0.2em]">
                Alternate: Apply for Access · Talk to an Expert
              </span>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const ProductBlock = ({ title, subtitle, accent, children }) => (
  <div
    className={`rounded-xl border ${
      accent === "blue" ? "border-[#1d4ed8]/30" : "border-[#dc2626]/30"
    } bg-[#0a0a0a] p-5 sm:p-6 space-y-5`}
  >
    <div>
      <div
        className={`text-[10px] font-bold tracking-[0.25em] uppercase ${
          accent === "blue" ? "text-[#1d4ed8]" : "text-[#dc2626]"
        }`}
      >
        {subtitle}
      </div>
      <div className="font-display font-bold text-lg sm:text-xl text-white mt-1">
        {title}
      </div>
    </div>
    {children}
  </div>
);
